import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "../../../design-system";
import { getDemoShareSrc, DEMO_SRC_EVENT } from "./demoSource";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * FlagshipFormats — the "runs on any big screen" device wall (bottom of the plugin page).
 *
 * Static, flat device mockups (NO tilt, NO hover motion — deliberately still) at true device
 * aspect ratios: iPad (4:3 landscape), Galaxy Z Fold unfolded (near-square inner display) and
 * folded (tall cover screen). Each runs the REAL live ET at that device's viewport, so you see the
 * same sheet reflow: the iPad + unfolded Fold get the full desktop editor (viewport > the 500px xs
 * breakpoint), the folded Fold gets the genuine mobile layout. The PHONE is intentionally NOT here —
 * it's the star of the hero.
 *
 * ISOLATION: self-contained (own dark device chrome via MUI sx), drops into either the .ccfp / .ccpp
 * page without touching their scoped CSS. Iframes are lazy — they only spin up once scrolled near.
 */

const font = "Outfit, sans-serif";

type Device = {
  key: string;
  label: string;
  caption: string;
  /** inner screen size (px) — sets the device aspect ratio */
  sw: number;
  sh: number;
  /** CSS viewport width the ET renders at (>500 = desktop sheet, <500 = mobile layout) */
  vw: number;
  bezel: number;
  radius: number;
  screenRadius: number;
  fold?: boolean;
  punch?: boolean;
  topCam?: boolean;
};

const DEVICES: Device[] = [
  // iPad portrait ≈ 3:4, logical width ~834
  { key: "ipad", label: "iPad", caption: "The full editor, portrait.", sw: 372, sh: 496, vw: 834, bezel: 14, radius: 28, screenRadius: 6, topCam: true },
  // Z Fold inner display, unfolded LANDSCAPE (near-square, slightly wide) — vertical crease
  { key: "fold-open", label: "Z Fold — unfolded", caption: "Landscape — every column, wide.", sw: 452, sh: 380, vw: 1180, bezel: 9, radius: 18, screenRadius: 6, fold: true, punch: true },
  // Z Fold cover screen ≈ 2.55 tall (narrow pocket phone)
  { key: "fold-closed", label: "Z Fold — folded", caption: "Pocket phone — the mobile sheet.", sw: 152, sh: 388, vw: 400, bezel: 9, radius: 28, screenRadius: 22, punch: true },
];

/** One device's screen: the live ET iframe, scaled from its true viewport into the screen box. */
const DeviceScreen: React.FC<{ src: string; d: Device }> = ({ src, d }) => {
  const scale = d.sw / d.vw;
  const [loaded, setLoaded] = useState(false);
  return (
    <Box sx={{ position: "relative", width: d.sw, height: d.sh, overflow: "hidden", background: "#0b0a0f", borderRadius: `${d.screenRadius}px` }}>
      {src ? (
        <iframe
          title={`CherryClipz — ${d.label}`}
          src={src}
          allow="fullscreen; clipboard-write"
          onLoad={() => setLoaded(true)}
          style={{
            position: "absolute", top: 0, left: 0, border: "none",
            width: d.vw, height: Math.round(d.sh / scale),
            transform: `scale(${scale})`, transformOrigin: "top left", background: "#0b0a0f",
          }}
        />
      ) : null}
      {!loaded && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#0b0a0f" }}>
          <img src={logoSite} alt="" width={28} style={{ opacity: 0.8 }} />
        </Box>
      )}
      {/* foldable inner-screen crease */}
      {d.fold && (
        <Box aria-hidden sx={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: "12px", transform: "translateX(-50%)", zIndex: 2, pointerEvents: "none", background: "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.22) 44%, rgba(255,255,255,0.05) 50%, rgba(0,0,0,0.22) 56%, rgba(0,0,0,0) 100%)" }} />
      )}
      {/* punch-hole camera */}
      {d.punch && (
        <Box aria-hidden sx={{ position: "absolute", top: "9px", left: "50%", transform: "translateX(-50%)", zIndex: 3, width: 7, height: 7, borderRadius: "50%", background: "#000", boxShadow: "0 0 0 1.5px rgba(255,255,255,0.08)" }} />
      )}
    </Box>
  );
};

const FlagshipFormats: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [shareSrc, setShareSrc] = useState<string>(getDemoShareSrc());

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    const onSrc = (e: Event) => {
      const next = (e as CustomEvent).detail;
      if (typeof next === "string" && next) setShareSrc(next);
    };
    window.addEventListener(DEMO_SRC_EVENT, onSrc);
    return () => window.removeEventListener(DEMO_SRC_EVENT, onSrc);
  }, []);

  const src = inView ? shareSrc : "";

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex", justifyContent: "center", alignItems: "flex-end", flexWrap: "wrap",
        gap: { xs: "34px", md: "26px" }, py: "6px",
      }}
    >
      {DEVICES.map((d) => (
        <Box key={d.key} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
          <Box
            sx={{
              position: "relative", padding: `${d.bezel}px`, background: "#15131b",
              borderRadius: `${d.radius}px`,
              boxShadow: "0 34px 70px -32px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            {/* iPad front camera on the top bezel */}
            {d.topCam && (
              <Box aria-hidden sx={{ position: "absolute", top: `${Math.max(3, (d.bezel - 6) / 2)}px`, left: "50%", transform: "translateX(-50%)", width: 6, height: 6, borderRadius: "50%", background: "#3a3540" }} />
            )}
            <DeviceScreen src={src} d={d} />
          </Box>
          <Box sx={{ textAlign: "center", maxWidth: `${d.sw + 40}px` }}>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "14px", color: "#e61d50", letterSpacing: "0.02em" }}>{d.label}</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: "#8d94a8", mt: "2px" }}>{d.caption}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default FlagshipFormats;
